import { useRequiredContext } from "@redotech/react-util/context";
import BoxIcon from "@redotech/redo-icons/box-01.svg";
import BoxIconFilled from "@redotech/redo-icons/box_filled.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import PackageFilledIcon from "@redotech/redo-icons/package_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  OrderManagementPermissions,
  ReturnsAndClaimsPermissions,
} from "@redotech/redo-model/permissions";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext } from "react";
import {
  BATCH_HISTORY_ICON,
  BATCH_HISTORY_ICON_FILLED,
  CLAIMS_ICON,
  CLAIMS_ICON_FILLED,
  EOD_MANIFEST_ICON,
  EOD_MANIFEST_ICON_FILLED,
  OrderManagementSuiteStatusesContext,
  PICK_SESSIONS_ICON,
  PICK_SESSIONS_ICON_FILLED,
  PRINT_JOBS_ICON,
  PRINT_JOBS_ICON_FILLED,
  RATE_CALCULATOR_ICON,
  RATE_CALCULATOR_ICON_FILLED,
  SCAN_ICON,
  SCAN_ICON_FILLED,
} from "../../../../redo-suite/order-management-cloud";
import { DashboardItem } from "../orders/dashboard";
import { FulfillmentItem } from "../orders/fulfillment";
import { PickupsItem } from "../orders/pickups";
import { WarehouseItem } from "../orders/warehouse";
import { ShippingAnalyticsItem } from "./shipping-analytics";

export function useShippingNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const pathRoot = routes.shippingBaseUrl;
  const { omsEnabled } = useContext(OrderManagementSuiteStatusesContext);

  const canViewShippingSection = useHasPermission(
    OrderManagementPermissions.VIEW_ORDER,
    OrderManagementPermissions.SCAN_AND_VERIFY,
    OrderManagementPermissions.MANAGE_SHIPMENTS,
  );

  if (!canViewShippingSection) {
    return null;
  }

  const primaryTab: LinkPrimaryTab = {
    id: "shipping",
    hasNotification: false,
    UnfilledIcon: PackageIcon,
    FilledIcon: PackageFilledIcon,
    type: "link",
    pathRoot,
    href: routes.shippingDefaultRoute,
    friendlyName: "Shipping",
  };

  const secondaryNavigationData: SecondaryNavigationData | undefined =
    omsEnabled
      ? {
          id: "shipping",
          name: "Shipping & fulfillments",
          NavContent: ShippingSecondaryNavigation,
        }
      : undefined;

  return { primaryTab, secondaryNavigationData, isActiveProduct: omsEnabled };
}

const ShippingSecondaryNavigation = memo(
  function ShippingSecondaryNavigation() {
    const { id: nodeId, TreeContext } = useNavTreeNode({
      friendlyName: "Shipping & fulfillments",
    });
    const routes = useRequiredContext(MerchantRoutesContext);
    const team = useContext(TeamContext);
    const { omsEnabled, wmsEnabled } = useContext(
      OrderManagementSuiteStatusesContext,
    );

    const isReturnEnabled = team?.settings.returns?.enabled;

    const canViewShippingTabs = useHasPermission(
      OrderManagementPermissions.MANAGE_SHIPMENTS,
    );
    const canViewScan = useHasPermission(
      OrderManagementPermissions.SCAN_AND_VERIFY,
    );
    const canManagePickSessions = useHasPermission(
      OrderManagementPermissions.MANAGE_PICK_SESSIONS,
    );
    const canManageCarrierClaims = useHasPermission(
      ReturnsAndClaimsPermissions.MANAGE_CARRIER_CLAIM,
    );

    const overboxLabelsEnabled =
      !!team?.settings.outboundLabels?.useApcCarrierAccount ||
      !!team?.settings.outboundLabels?.useEpostGlobalCarrierAccount;

    return (
      <TreeContext.Provider value={nodeId}>
        <DashboardItem />
        <ShippingAnalyticsItem />
        <FulfillmentItem />
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: routes.getOrdersOutboundLabelsSubRoute("batch-history"),
          }}
          FilledIcon={BATCH_HISTORY_ICON_FILLED}
          friendlyName="Batch history"
          hidden={!omsEnabled || !canViewShippingTabs}
          id="batch-history"
          UnfilledIcon={BATCH_HISTORY_ICON}
        />
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.shippingCarrierClaimsHref }}
          FilledIcon={CLAIMS_ICON_FILLED}
          friendlyName="Carrier claims"
          hidden={!((omsEnabled || isReturnEnabled) && canManageCarrierClaims)}
          id="carrier-claims"
          UnfilledIcon={CLAIMS_ICON}
        />
        {omsEnabled && <PickupsItem />}
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: routes.getOrdersOutboundLabelsSubRoute("eod-manifest"),
          }}
          FilledIcon={EOD_MANIFEST_ICON_FILLED}
          friendlyName="EOD manifest"
          hidden={!omsEnabled || !canViewShippingTabs}
          id="eod-manifest"
          UnfilledIcon={EOD_MANIFEST_ICON}
        />
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: routes.getOrdersOutboundLabelsSubRoute("overbox-labels"),
          }}
          FilledIcon={BoxIconFilled}
          friendlyName="Overbox labels"
          hidden={!omsEnabled || !canViewShippingTabs || !overboxLabelsEnabled}
          id="overbox-labels"
          UnfilledIcon={BoxIcon}
        />
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.pickSessionTableHref }}
          FilledIcon={PICK_SESSIONS_ICON_FILLED}
          friendlyName="Pick sessions"
          hidden={!omsEnabled || !wmsEnabled || !canManagePickSessions}
          id="pick-sessions"
          UnfilledIcon={PICK_SESSIONS_ICON}
        />
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: routes.getOrdersOutboundLabelsSubRoute("print-jobs"),
          }}
          FilledIcon={PRINT_JOBS_ICON_FILLED}
          friendlyName="Print jobs"
          hidden={!omsEnabled || !canViewShippingTabs}
          id="print-jobs"
          UnfilledIcon={PRINT_JOBS_ICON}
        />
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: routes.getOrdersOutboundLabelsSubRoute("rate-calculator"),
          }}
          FilledIcon={RATE_CALCULATOR_ICON_FILLED}
          friendlyName="Rate calculator"
          hidden={!omsEnabled || !canViewShippingTabs}
          id="rate-calculator"
          UnfilledIcon={RATE_CALCULATOR_ICON}
        />
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: routes.getOrdersOutboundLabelsSubRoute("scan"),
          }}
          FilledIcon={SCAN_ICON_FILLED}
          friendlyName="Scan"
          hidden={!omsEnabled || !canViewScan}
          id="scan"
          UnfilledIcon={SCAN_ICON}
        />
        <WarehouseItem />
      </TreeContext.Provider>
    );
  },
);
